import React from 'react';
import { AlertBanner, Button, InputTextArea, Spinner, TagGeneral, Text, TooltipTrigger } from '@spark';
import JsonTreeViewer from './JsonTreeViewer';
import ClassificationSummary from './ClassificationSummary';
import SummarizationSummary from './SummarizationSummary';
import NerSummary from './NerSummary';
import ResponseStepCard from './ResponseStepCard';
import ResponseTypeSelector from './ResponseTypeSelector';
import { TaskType, isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';

/** True when the raw output only parsed after lenient JSON repair. */
const wasJsonRepaired = (rawResponse, parsedJson) => {
    if (!rawResponse || parsedJson == null) return false;
    try {
        JSON.parse(rawResponse);
        return false;
    } catch {
        return true;
    }
};

const ModelOutputStep = ({
    onInspectLogic,
    onGenerateResponse,
    generatingResponse,
    rawResponseError,
    responseType,
    rawResponse,
    jsonParseError,
    parsedJson,
    taskType,
    isMultilabel,
    showHighlightInstruction,
    // JSON Viewer props
    isNerJsonMode,
    selectedJsonTokens,
    nerPathAssignments,
    toggleJsonSelection,
    handleRemoveNerAssignment,
    buildNerDropdownMenu,
    jsonFieldMetaMap,
    getLabelFromFieldPath,
    // Classification Summary props
    normalizedJsonLabelField,
    jsonLabelFieldOptions,
    setSelectedJsonLabelField,
    multilabelDelimiter,
    setMultilabelDelimiter,
    singleLabelListMode,
    setSingleLabelListMode,
    getJsonValueByPath,
    formatNerAssignedValue,
    splitLabels,
    delimiterOptions,
    getValuesByPath,
    initialParserSelections, // Added prop
    // NER Summary props
    handleManualNerAssignment,
    jsonFieldSelectOptions,
    entityFieldConfig,
    relationFieldConfig,
    supportsRelationFields,
    nerFieldAssignments,
    // Playground Inspect modal reuse: no step badge, no Run button (the raw
    // output comes from an already-generated playground cell), and an
    // optional node rendered at the top of the body (Response type row).
    // Model Setup toolbar owns Response type + Inspect logic; Shuffle lives
    // on Sample Input and Run lives on this card unless hideRunButton.
    hideStepBadge = false,
    hideRunButton = false,
    hideInspectLogic = false,
    beforeContent = null,
    runValidationError = null,
    // Re-eval modal: keep Run, freeze mapping that drives the parser function.
    mappingReadOnly = false,
    // Set while the sample runs as a batch job (batch-only deployment):
    // informational banner so the empty output box is not read as a failure.
    batchNotice = null,
    // Model Setup: "Response type" select rendered in the card header
    // (Figma Output card). Hidden when the host renders its own control.
    showResponseTypeControl = false,
    onResponseTypeChange = null,
    showJsonResponseTypeOption = true,
}) => {

    // Content generation with no mapped field: the whole JSON output is the
    // response, so the raw-output area carries the same selected indication a
    // single mapped token gets.
    const isEntireResponseSelected = taskType === TaskType.contentGeneration
        && responseType === 'json'
        && !!parsedJson
        && normalizedJsonLabelField == null;

    const renderJsonViewer = () => {
        if (!rawResponse) {
            return null;
        }
        if (!parsedJson && rawResponse) {
            return (
                <>
                    {jsonParseError && (
                        <div style={{ marginTop: -10 }}>
                            <AlertBanner
                                feedback="warning"
                                showButton={false}
                                showCloseButton={false}
                                text={<div style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>Invalid JSON<div style={{ fontSize: '12px', color: '#6e6e6e' }}>{jsonParseError}</div></div>}
                            />
                        </div>
                    )}
                    <div>
                        <pre className="response-step-card__json-fallback-pre optune-scrollbar">
                            {rawResponse || 'No response yet'}
                        </pre>
                    </div>
                </>
            );
        }

        const treeViewer = (
            <JsonTreeViewer
                data={parsedJson}
                isNerJsonMode={isNerJsonMode}
                selectedJsonTokens={selectedJsonTokens}
                nerPathAssignments={nerPathAssignments}
                toggleJsonSelection={toggleJsonSelection}
                handleRemoveNerAssignment={handleRemoveNerAssignment}
                buildNerDropdownMenu={buildNerDropdownMenu}
                jsonFieldMetaMap={jsonFieldMetaMap}
                getLabelFromFieldPath={getLabelFromFieldPath}
                taskType={taskType}
                readOnly={mappingReadOnly}
            />
        );

        if (!isEntireResponseSelected) {
            return treeViewer;
        }

        return (
            <div className="json-viewer-entire-response">
                <Text
                    variant="detail-regular"
                    color="secondary"
                    as="div"
                    className="json-viewer-entire-response__note fs-unmask"
                >
                    Entire response selected
                </Text>
                {treeViewer}
            </div>
        );
    };

    const renderClassificationSummary = () => {
        if (taskType !== TaskType.classification) return null;

        return (
            <ClassificationSummary
                taskType={taskType}
                isMultilabel={isMultilabel}
                responseType={responseType}
                rawResponse={rawResponse}
                parsedJson={parsedJson}
                normalizedJsonLabelField={normalizedJsonLabelField}
                jsonLabelFieldOptions={jsonLabelFieldOptions}
                setSelectedJsonLabelField={setSelectedJsonLabelField}
                multilabelDelimiter={multilabelDelimiter}
                setMultilabelDelimiter={setMultilabelDelimiter}
                singleLabelListMode={singleLabelListMode}
                setSingleLabelListMode={setSingleLabelListMode}
                selectedJsonTokens={selectedJsonTokens}
                getJsonValueByPath={getJsonValueByPath}
                formatNerAssignedValue={formatNerAssignedValue}
                splitLabels={splitLabels}
                delimiterOptions={delimiterOptions}
                getValuesByPath={getValuesByPath}
                initialParserSelections={initialParserSelections}
                readOnly={mappingReadOnly}
            />
        );
    };

    const renderSummarizationSummary = () => {
        if (!isGenerativeTask(taskType)) return null;

        return (
            <SummarizationSummary
                taskType={taskType}
                responseType={responseType}
                rawResponse={rawResponse}
                parsedJson={parsedJson}
                normalizedJsonLabelField={normalizedJsonLabelField}
                jsonLabelFieldOptions={jsonLabelFieldOptions}
                setSelectedJsonLabelField={setSelectedJsonLabelField}
                selectedJsonTokens={selectedJsonTokens}
                getJsonValueByPath={getJsonValueByPath}
                formatNerAssignedValue={formatNerAssignedValue}
                getValuesByPath={getValuesByPath}
                initialParserSelections={initialParserSelections}
                readOnly={mappingReadOnly}
            />
        );
    };

    const showHeaderActions = !hideInspectLogic || showResponseTypeControl;
    const repaired = responseType === 'json' && wasJsonRepaired(rawResponse, parsedJson);

    return (
        <ResponseStepCard
            stepNumber={hideStepBadge ? null : <div className="fs-unmask">2</div>}
            title={<div className="fs-unmask">Output</div>}
            extra={showHeaderActions ? (
                <>
                    {!hideInspectLogic && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={onInspectLogic}
                            className="response-step-card__inspect-logic fs-unmask"
                        >
                            Inspect logic
                        </Button>
                    )}
                    {showResponseTypeControl && (
                        <ResponseTypeSelector
                            taskType={taskType}
                            responseType={responseType}
                            onChange={onResponseTypeChange}
                            variant="toolbar"
                            showJsonOption={showJsonResponseTypeOption}
                            disabled={mappingReadOnly}
                        />
                    )}
                </>
            ) : null}
        >
            {beforeContent}
            <div className="response-step-card__output-head fs-unmask">
                <div className="response-step-card__output-head-row">
                    <div className="response-step-card__output-title">
                        <Text variant="base-medium" color="primary" as="span">
                            What the model returns
                        </Text>
                        {repaired && (
                            <TooltipTrigger text="The raw output was not valid JSON and was repaired before parsing.">
                                <TagGeneral label="Repaired" className="response-step-card__repaired-tag" />
                            </TooltipTrigger>
                        )}
                    </div>
                    {!hideRunButton && (
                        <div className="response-interpreter__run">
                            <TooltipTrigger text="Run to check the output of the commercial model">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="small"
                                    startIcon={generatingResponse ? <Spinner size="small" /> : undefined}
                                    onClick={onGenerateResponse}
                                    disabled={generatingResponse}
                                    disableWhenReadOnly
                                    aria-busy={generatingResponse}
                                    className="fs-unmask"
                                >
                                    {generatingResponse ? 'Running…' : 'Run'}
                                </Button>
                            </TooltipTrigger>
                            {runValidationError && (
                                <Text
                                    variant="detail-regular"
                                    as="div"
                                    className="response-interpreter__run-error fs-unmask"
                                    role="alert"
                                >
                                    {runValidationError}
                                </Text>
                            )}
                        </div>
                    )}
                </div>
                <Text variant="detail-regular" color="secondary" as="div">
                    The raw output before parsing.
                </Text>
            </div>
            {batchNotice && (
                <div style={{ marginBottom: 12 }} className="fs-unmask">
                    <AlertBanner
                        feedback="info"
                        showButton={false}
                        showCloseButton={false}
                        text={batchNotice}
                    />
                </div>
            )}
            {rawResponseError && (
                <div style={{ marginBottom: 12 }}>
                    <AlertBanner
                        feedback="error"
                        showButton={false}
                        showCloseButton={false}
                        text={(
                            <div>
                                <div>Unable to fetch response</div>
                                <div>{rawResponseError}</div>
                            </div>
                        )}
                    />
                </div>
            )}
            {responseType === 'json'
                ? renderJsonViewer()
                : (
                    <>
                        <InputTextArea
                            showLabel={false}
                            value={rawResponse || ''}
                            readOnly
                            placeholder="Run to see the raw response from your teacher model"
                            rows={4}
                            className="response-step-card__plain-textarea"
                        />
                    </>
                )}
            {showHighlightInstruction && !mappingReadOnly && responseType === 'json' && (
                <div className="response-step-card__selected-labels">
                    {rawResponse ? (
                        <Text className='fs-unmask' variant="base-regular" color="secondary" style={{ textAlign: 'center' }}>
                            Highlight text above to select {taskType === TaskType.classification ? (isMultilabel ? 'labels' : 'label') : isGenerativeTask(taskType) ? getGenerativeOutputNoun(taskType) : 'field'}
                        </Text>
                    ) : (
                        <Text className='fs-unmask' variant="base-regular" color="secondary" style={{ textAlign: 'center' }}>Click "Run" to see the raw response from your teacher model</Text>
                    )}
                </div>
            )}

            {renderClassificationSummary()}
            {renderSummarizationSummary()}

            <NerSummary
                isNerJsonMode={isNerJsonMode}
                nerFieldAssignments={nerFieldAssignments}
                handleManualNerAssignment={handleManualNerAssignment}
                jsonFieldSelectOptions={jsonFieldSelectOptions}
                entityFieldConfig={entityFieldConfig}
                relationFieldConfig={relationFieldConfig}
                supportsRelationFields={supportsRelationFields}
                readOnly={mappingReadOnly}
            />
        </ResponseStepCard>
    );
};

export default ModelOutputStep;
